import React, { useEffect, useState } from 'react';
import Loader from './Loader';

/**
 * Full-page Statbox branded loader. Centered card containing the 100px
 * branded mark, a "Loading" title, and an optional subtitle that describes
 * what's actually being fetched (e.g. "Pulling events and stats").
 *
 * Positioning: the card is always centered geometrically — both
 * horizontally and vertically — within its available height. Full-page
 * loaders occupy 100vh; contained loaders occupy their parent's height.
 *
 * Anti-flicker: by default the loader waits `delay` ms before showing
 * anything. If the parent unmounts LoaderScreen before the delay elapses
 * (i.e. the data resolved fast enough), the user sees nothing — no flash.
 * Pass `delay={0}` to opt out and render immediately (e.g. user-initiated
 * submit overlays where instant feedback is preferred).
 *
 * Props:
 *   title       (string, default "Loading")
 *   subtitle    (string, optional)
 *   fullHeight  (boolean, default true) — true: occupy 100vh, false: parent height
 *   className   (string, optional)      — pass through to the wrapper
 *   delay       (number, default 250)   — ms to wait before showing the loader
 */
const LoaderScreen = ({
  title = 'Loading',
  subtitle,
  fullHeight = true,
  className = '',
  delay = 250,
}) => {
  // Gate the visible loader behind a delay so quick loads never flash.
  // delay === 0 short-circuits to immediate-show.
  const [shouldShow, setShouldShow] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return undefined;
    const t = setTimeout(() => setShouldShow(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  if (!shouldShow) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col items-center justify-center w-full bg-[#F5F6F8] ${className}`}
      style={{
        minHeight: fullHeight ? '100vh' : '100%',
        height: fullHeight ? undefined : '100%',
      }}
    >
      <div
        className="flex flex-col items-center justify-center text-center px-6 py-8 text-[#F5F6F8]"
        style={{ maxWidth: '400px' }}
      >
        <Loader size={100} />
        <p className="mt-5 text-[15px] font-medium text-slate-900">
          {title}
        </p>
        {subtitle && (
          <p className="mt-1 text-[13px] text-slate-500">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
};

export default LoaderScreen;
